import { CaretLeftIcon } from "@phosphor-icons/react/dist/csr/CaretLeft";
import { CaretRightIcon } from "@phosphor-icons/react/dist/csr/CaretRight";
import type { Table } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Input } from "@volca/ui/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@volca/ui/components/ui/select";
import { Stack } from "@volca/ui/components/ui/stack";

export const TablePagination = <TData,>({
  table,
  totalRowCount,
}: {
  table: Table<TData>;
  totalRowCount?: number;
}) => {
  const pageSizeOptions = [10, 25, 50, 100].map((size) => ({
    value: size.toString(),
    label: size.toString(),
  }));
  const currentPage = table.getState().pagination.pageIndex + 1;
  const totalPages = table.getPageCount();
  const pageSize = table.getState().pagination.pageSize;
  const displayedRows =
    totalRowCount ?? table.getFilteredRowModel().rows.length;
  const startRow =
    displayedRows === 0
      ? 0
      : table.getState().pagination.pageIndex * pageSize + 1;
  const endRow =
    displayedRows === 0 ? 0 : Math.min(startRow + pageSize - 1, displayedRows);
  const [pageInput, setPageInput] = useState(currentPage.toString());

  useEffect(() => {
    setPageInput(currentPage.toString());
  }, [currentPage]);

  const handlePageInputChange = (value: string) => {
    setPageInput(value);
    const page = parseInt(value, 10);
    if (!isNaN(page) && page >= 1 && page <= totalPages) {
      table.setPageIndex(page - 1);
    }
  };

  const handlePageInputBlur = () => {
    setPageInput(currentPage.toString());
  };

  return (
    <Stack
      gap={4}
      className="w-full flex-wrap gap-y-2 border-t px-4 py-3"
      direction="row"
      align="center"
      justify="between"
    >
      <Stack gap={2} align="center" direction="row">
        <span aria-hidden className="hidden text-xs text-subtle sm:inline">
          Rows per page
        </span>
        <Select
          value={pageSize.toString()}
          onValueChange={(value) => {
            if (value) table.setPageSize(Number(value));
          }}
          items={pageSizeOptions}
        >
          <SelectTrigger
            aria-label={`Rows per page: ${pageSize}`}
            className="w-18 data-[size=default]:h-9 sm:data-[size=default]:h-8"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {pageSizeOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
        <span className="pl-2 text-xs whitespace-nowrap text-subtle">
          {startRow}–{endRow} of {displayedRows}
        </span>
      </Stack>

      <Stack gap={2} direction="row" align="center">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="size-9 sm:size-8"
          aria-label="Previous page"
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
        >
          <CaretLeftIcon />
        </Button>
        <Stack direction="row" align="center" gap={1}>
          <span className="text-xs text-subtle">Page</span>
          <Input
            aria-label="Page number"
            value={pageInput}
            onChange={(e) => handlePageInputChange(e.target.value)}
            onBlur={handlePageInputBlur}
            className="h-9 w-12 px-1 text-center sm:h-8"
          />
          <span className="text-xs text-subtle">of {totalPages}</span>
        </Stack>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="size-9 sm:size-8"
          aria-label="Next page"
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
        >
          <CaretRightIcon />
        </Button>
      </Stack>
    </Stack>
  );
};
